import type { PixelClient } from "../client.js";
import { ActiveTimeTracker, attachActiveListeners } from "./active-time.js";
import { attachClickTracking } from "./clicks.js";
import { attachPageLeave } from "./page-leave.js";
import { pageContext, pageProps } from "./page-context.js";
import { attachProductEngagement, type ProductPayload } from "./product-engagement.js";
import { attachScrollDepth } from "./scroll-depth.js";
import { WOOCOMMERCE_SELECTORS } from "./selectors.js";
import { startPageSession } from "./session.js";

/**
 * Config the WooCommerce PHP plugin injects via wp_localize_script as window.RetargetConfig.
 * `product` is present only on a single-product page; prices are ALREADY minor units (the plugin
 * converts with wc_get_price_decimals so the browser never does float math).
 */
export interface WooProduct extends ProductPayload {}

export interface WooCollection {
  collection_id: string;
  collection_title?: string;
  collection_type?: string;
}

export interface WooPageSignals {
  /** Single-product page payload, else null. */
  product: WooProduct | null;
  /** Product category or shop archive, else null. */
  collection: WooCollection | null;
  /** True on the cart page. */
  isCart: boolean;
  /** True on the checkout page. */
  isCheckout: boolean;
}

/**
 * Wire DOM + WooCommerce events to the client. Idempotent per page load. Returns a teardown fn.
 */
export function attachWooCommerce(client: PixelClient, signals: WooPageSignals): () => void {
  const cleanups: Array<() => void> = [];
  const activeTime = new ActiveTimeTracker();

  cleanups.push(attachActiveListeners(activeTime));
  cleanups.push(attachScrollDepth(client, () => activeTime.markActive()));
  cleanups.push(attachClickTracking(client, signals.product?.product_id));
  cleanups.push(attachPageLeave(client, activeTime));

  startPageSession(client);
  void client.track("pageview", pageProps(), pageContext());

  if (signals.collection) {
    void client.track("collection_view", { ...signals.collection }, pageContext());
  }

  if (signals.product) {
    void client.track("product_view", { ...signals.product }, pageContext());
    cleanups.push(attachProductEngagement(client, signals.product, WOOCOMMERCE_SELECTORS, activeTime));
  }

  if (signals.isCart) {
    void client.track("cart_view", {}, pageContext());
  }
  if (signals.isCheckout) {
    void client.track("checkout_start", {}, pageContext());
  }

  const onSubmit = (e: Event) => {
    const form = (e.target as HTMLElement | null)?.closest?.("form.cart");
    if (!form) return;
    const props = signals.product ? { ...signals.product } : {};
    void client.track("add_to_cart", props, pageContext());
  };
  document.addEventListener("submit", onSubmit, true);
  cleanups.push(() => document.removeEventListener("submit", onSubmit, true));

  const onAjaxAdd = () => {
    const props = signals.product ? { ...signals.product } : {};
    void client.track("add_to_cart", props, pageContext());
  };
  document.body.addEventListener("added_to_cart", onAjaxAdd as EventListener);
  cleanups.push(() => document.body.removeEventListener("added_to_cart", onAjaxAdd as EventListener));

  return () => {
    for (const fn of cleanups) fn();
  };
}
